import { useEffect, useMemo, useState, type FC, type ReactNode } from 'react';
import fileDownload from 'js-file-download';

import {
  type Evaluation,
  type EvaluationCheckpoint,
  type EvaluationMetadata,
} from '@/types';
import CheckpointStatusIcon from '../CheckpointStatusIcon';
import { Tag, Tooltip, TooltipBody } from '@wk/components-react16';
import { copyToClipboard } from '@/lib/utils';
import {
  getDaysLeftText,
  getUnscoredStatusDetailsFromMetadata,
  normalizeUnscoredType,
} from '@/lib/unscoredStatus';
import { format } from 'date-fns';
import { fetchEvaluationExcel } from '@/domains/evaluation/evaluationApi';
import { getStandardsForCheckpoint } from '@/domains/checkpoint/checkpointStandardsApi';
import {
  analyzeCheckpoint,
  pollAnalysisResult,
} from '@/domains/aiAnalysis/aiAnalysisApi';
import useToast from '@/shared/stores/toastStore';
import useHubStandardsStore from '@/stores/hubStandards';
import {
  AI_ANALYZER_API_URL,
  HUB_API_URL,
  SITE_COMPLIANCE_PORTAL_ENABLED,
} from '@/config/env';

import AssessmentData from '../AssessmentDataDrawer';

import ReportBug from '@/features/reportBug/components/ReportBugDrawer';
import { type EvaluationView } from '../../lib/viewAccess';
import CheckpointResultActions from './CheckpointResultActions';
import CheckpointResultBody from './CheckpointResultBody';
import CheckpointResultPanels from './CheckpointResultPanels';

import './CheckpointResult.css';

interface CheckpointResultProps {
  checkpoint: EvaluationCheckpoint | null;
  activeView: EvaluationView;
  availableViews: EvaluationView[];
  evaluationId: string;
  bitId: string;
  evaluation: Evaluation | null;
  canOpenAssessmentData?: boolean;
  exportCsvLabel?: string;
  onExportCsvOverride?: () => Promise<void>;
  onViewChange: (view: EvaluationView) => void;
  renderAssessmentDataDrawer?: (props: {
    checkpoint: EvaluationCheckpoint;
    evaluation: Evaluation | null;
    isOpen: boolean;
    onClose: () => void;
  }) => ReactNode;
}

interface CheckpointResultState {
  isExportingCsv: boolean;
  isRemarksOpen: boolean;
  isAssessmentDataOpen: boolean;
  isExceptionDetailsOpen: boolean;
  isReportBugOpen: boolean;
  isActionsDropdownOpen: boolean;
  isAiPanelOpen: boolean;
}

const MOBILE_MEDIA_QUERY = '(max-width: 47.9375rem)';

const AI_ANALYSIS_ALLOWED_CHECKPOINT_IDS = new Set([
  'ORR-CHK-045',
  'ORR-CHK-044',
  'ORR-CHK-042',
]);

const CheckpointResult: FC<CheckpointResultProps> = ({
  checkpoint,
  activeView,
  availableViews,
  evaluationId,
  bitId,
  evaluation,
  canOpenAssessmentData = false,
  exportCsvLabel,
  onExportCsvOverride,
  onViewChange,
  renderAssessmentDataDrawer,
}) => {
  const [state, setState] = useState<CheckpointResultState>({
    isExportingCsv: false,
    isRemarksOpen: false,
    isAssessmentDataOpen: false,
    isExceptionDetailsOpen: false,
    isReportBugOpen: false,
    isActionsDropdownOpen: false,
    isAiPanelOpen: false,
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<ReactNode>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });
  const standardsData = useHubStandardsStore((storeState) => storeState.standardsData);
  const fetchStandards = useHubStandardsStore((storeState) => storeState.fetchStandards);
  const canSwitchEvaluationView =
    !SITE_COMPLIANCE_PORTAL_ENABLED && availableViews.length > 1;

  const { showToast } = useToast();

  const isAiAnalysisEnabled = useMemo(
    () => Boolean(AI_ANALYZER_API_URL?.trim()),
    [],
  );

  const hubDocsUrl = useMemo(() => {
    const baseUrl = HUB_API_URL?.trim();
    if (!baseUrl) return null;
    return `${baseUrl.replace(/\/+$/, '')}/`;
  }, []);

  const hasEvaluationCheckpoints = useMemo(() => {
    return (evaluation?.checkpoints?.length ?? 0) > 0;
  }, [evaluation?.checkpoints?.length]);

  const isAllowedAiCheckpoint = useMemo(() => {
    const checkpointId = checkpoint?.metadata?.id?.toString() ?? '';
    return AI_ANALYSIS_ALLOWED_CHECKPOINT_IDS.has(checkpointId);
  }, [checkpoint?.metadata?.id]);

  const isFailedCheckpoint = checkpoint?.status === 'FAIL' || checkpoint?.status === 'ERROR';

  const shouldShowAiAnalysisAction =
    isAiAnalysisEnabled && !SITE_COMPLIANCE_PORTAL_ENABLED;
  const canRunAiAnalysis =
    isAiAnalysisEnabled &&
    hasEvaluationCheckpoints &&
    isAllowedAiCheckpoint &&
    isFailedCheckpoint &&
    Boolean(checkpoint?.metadata?.id);

  const handleStateUpdate = (newState: Partial<CheckpointResultState>) => {
    setState((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const openCheckpointNavigator = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('evaluation-details:open-mobile-details'),
    );
  };

  useEffect(() => {
    // O portal Site Compliance nao usa os standards do Pegasus Hub.
    // Pular o fetch evita o token/consent da API do Hub.
    if (SITE_COMPLIANCE_PORTAL_ENABLED) {
      return;
    }

    fetchStandards().catch((error) =>
      console.error('Error fetching checkpoint standards', error),
    );
  }, [fetchStandards]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', onViewportChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const openActionsMenu = () => {
      setState((prev) => ({ ...prev, isActionsDropdownOpen: true }));
    };

    window.addEventListener('evaluation:open-checkpoint-actions', openActionsMenu);

    return () => {
      window.removeEventListener('evaluation:open-checkpoint-actions', openActionsMenu);
    };
  }, []);

  const onShareClick = () => {
    copyToClipboard(window.location.href);
    // state.isActionsDropdownOpen = false;
  };
  const RunAiAnalysis = async () => {
    if (!isAiAnalysisEnabled) {
      showToast('error', {
        title: 'AI analysis not available',
        message: 'AI analysis is not available in this environment.',
      });
      return;
    }

    if (!hasEvaluationCheckpoints) {
      showToast('error', {
        title: 'Unable to run AI analysis',
        message: 'This evaluation response does not include checkpoints.',
      });
      return;
    }

    if (!isAllowedAiCheckpoint) {
      showToast('error', {
        title: 'AI analysis not available',
        message: 'AI analysis is only available for specific checkpoints.',
      });
      return;
    }

    if (!checkpoint?.metadata.id) {
      showToast('error', {
        title: 'Unable to run AI analysis',
        message: 'Checkpoint ID is missing',
      });
      return;
    }

    if (checkpoint.status !== 'FAIL' && checkpoint.status !== 'ERROR') {
      showToast('error', {
        title: 'AI analysis not available',
        message: 'AI analysis can only be run for failed checkpoints.',
      });
      return;
    }

    setAiLoading(true);
    setAiResult(null);
    handleStateUpdate({ isAiPanelOpen: true });

    try {
      const analysisResponse = await analyzeCheckpoint({
        evaluation_id: evaluationId,
        checkpoint_id: checkpoint.metadata.id,
        save_to_storage: true,
      });

      const { analysis_id } = analysisResponse.data;

      const result = await pollAnalysisResult(analysis_id);

      if (result.status === 'completed') {

        const formatText = (value: unknown) => {
          if (value === null || value === undefined) return 'N/A';
          if (typeof value === 'string') return value;
          if (typeof value === 'number' || typeof value === 'boolean') return String(value);
          try {
            return JSON.stringify(value, null, 2);
          } catch {
            return String(value);
          }
        };

        const formatList = (value: unknown) => {
          if (value === null || value === undefined) return 'N/A';
          if (Array.isArray(value)) {
            if (value.length === 0) return 'N/A';
            return value.map((v) => `- ${String(v)}`).join('\n');
          }
          return formatText(value);
        };

        const findFirstByKey = (
          value: unknown,
          keys: Set<string>,
          maxDepth: number,
          depth: number = 0,
        ): unknown => {
          if (depth > maxDepth) return undefined;
          if (!value || typeof value !== 'object') return undefined;

          if (Array.isArray(value)) {
            for (const item of value) {
              const found = findFirstByKey(item, keys, maxDepth, depth + 1);
              if (found !== undefined) return found;
            }
            return undefined;
          }

          const record = value as Record<string, unknown>;
          for (const [k, v] of Object.entries(record)) {
            if (keys.has(k) && v !== undefined) return v;
          }

          for (const v of Object.values(record)) {
            const found = findFirstByKey(v, keys, maxDepth, depth + 1);
            if (found !== undefined) return found;
          }

          return undefined;
        };

        const sourceFindings = (result.source_findings ?? {}) as Record<string, unknown>;
        const orca =
          (sourceFindings.orca as unknown) ??
          (sourceFindings.ORCA as unknown) ??
          (sourceFindings.Orca as unknown) ??
          sourceFindings;

        const title =
          result.remediation_script?.title ??
          result.title ??
          (findFirstByKey(orca, new Set(['title', 'Title']), 6) as string | undefined);

        const riskLevel = result.risk_level ?? result.risk_assessment?.risk_level;

        const affectedResources = result.affected_resources;

        const recommendationsFromOrca = findFirstByKey(
          orca,
          new Set(['recommendations', 'Recommendations', 'recommendation', 'Recommendation']),
          6,
        );

        const jsonRecommendations = result.recommendations;
        const hasOrcaRecommendations =
          Array.isArray(recommendationsFromOrca)
            ? recommendationsFromOrca.length > 0
            : Boolean(recommendationsFromOrca);

        const recommendationsText = hasOrcaRecommendations
          ? `ORCA:\n${formatList(recommendationsFromOrca)}\n\nJSON:\n${formatList(jsonRecommendations)}`
          : formatList(jsonRecommendations);

        const scriptContent = result.remediation_script?.script_content;

        const evidence = result.evidence ?? undefined;
        const evidenceWithoutAiReasoning = evidence
          ? Object.fromEntries(
            Object.entries(evidence).filter(([key]) => key !== 'ai_reasoning'),
          )
          : undefined;
        const aiReasoning = evidence?.ai_reasoning;

        setAiResult(
          <span>
            <strong>TITLE</strong>
            {'\n'}
            {formatText(title)}
            {'\n\n'}

            <strong>RISK LEVEL</strong>
            {'\n'}
            {formatText(riskLevel)}
            {'\n\n'}

            <strong>AFFECTED RESOURCES</strong>
            {'\n'}
            {formatList(affectedResources)}
            {'\n\n'}

            <strong>ROOT CAUSE</strong>
            {'\n'}
            {formatText(result.root_cause)}
            {'\n\n'}

            <strong>RECOMMENDATIONS</strong>
            {'\n'}
            {recommendationsText}
            {'\n\n'}

            <strong>REMEDIATION SCRIPT</strong>
            {'\n'}
            {formatText(scriptContent)}
            {'\n\n'}

            <strong>SOURCE FINDINGS</strong>
            {'\n'}
            {formatText(result.source_findings)}
            {'\n\n'}

            <strong>EVIDENCE</strong>
            {'\n'}
            {formatText(evidenceWithoutAiReasoning)}
            {'\n\n'}

            <strong>AI REASONING</strong>
            {'\n'}
            {formatText(aiReasoning)}
          </span>
        );
        showToast('success', {
          title: 'AI Analysis completed',
          message: 'Analysis results are now available',
        });
      } else if (result.status === 'failed') {
        throw new Error(result.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      let errorMessage = 'Failed to analyze checkpoint';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              error?: string;
            };
          };
        };
        errorMessage = axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          `Server error (${axiosError.response?.status})`;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setAiResult(`AI Analysis Failed\n\nError: ${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
      showToast('error', {
        title: 'AI Analysis failed',
        message: errorMessage,
      });
    } finally {
      setAiLoading(false);
    }
  }


  const exportCsv = async () => {
    handleStateUpdate({ isExportingCsv: true });
    try {
      if (onExportCsvOverride) {
        await onExportCsvOverride();
        handleStateUpdate({ isExportingCsv: false });
        return;
      }

      const exportView = activeView === 'dr' ? 'dr' : 'default';
      const res = await fetchEvaluationExcel(evaluationId, exportView);
      const blob = await res.blob();
      fileDownload(blob, `ORR-Evaluation-Report-${bitId}.xlsx`);
      handleStateUpdate({ isExportingCsv: false });
      //   state.isActionsDropdownOpen = false;
    } catch {
      showToast('error', {
        title: 'Failed to export evaluation report',
      });
      handleStateUpdate({ isExportingCsv: false });
    }
  };

  const activeCheckpointMetadata = useMemo(() => {
    const excludeKeys = [
      'url',
      'displayName',
      'scorePercent',
      'totalControls',
      'totalPreviewControls',
      'totalExemptedAssessments',
      'totalAssessments',
      'maintenance',
      'unscoredStatusDetails',
      'UnscoredStatusDetails',
      'parameters',
    ];
    const replaceKeys: Partial<Record<string, string>> = {
      checkpointVersion: 'version',
    };

    if (!checkpoint) return [];

    // Always surface the checkpoint identifier (id/name) first so the reader
    // knows which checkpoint they are looking at before any other metadata.
    const priorityKeys = ['id', 'name'];
    const keys = Object.keys(checkpoint.metadata).filter(
      (key) => !excludeKeys.includes(key),
    );
    keys.sort((a, b) => {
      const aPriority = priorityKeys.indexOf(a);
      const bPriority = priorityKeys.indexOf(b);
      if (aPriority !== -1 && bPriority === -1) return -1;
      if (aPriority === -1 && bPriority !== -1) return 1;
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      return 0;
    });
    const metadata = keys.map((key) => {
      return {
        key: replaceKeys[key] ?? key,
        value: checkpoint?.metadata[
          key as keyof EvaluationMetadata
        ] as string,
      };
    });
    return metadata;
  }, [checkpoint]);

  const shouldShowHelp = useMemo(() => {
    return Boolean(checkpoint?.metadata.url) && checkpoint?.status === 'FAIL';
  }, [checkpoint]);

  const standardsForCheckpoint = useMemo(() => {
    const checkpointId = checkpoint?.metadata.id?.toString();
    const checkpointVersion =
      checkpoint?.metadata.checkpointVersion ?? checkpoint?.metadata.version;

    return getStandardsForCheckpoint(
      checkpointId,
      standardsData,
      checkpointVersion,
    );
  }, [
    checkpoint?.metadata.id,
    checkpoint?.metadata.checkpointVersion,
    checkpoint?.metadata.version,
    standardsData,
  ]);

  const hasStandards = standardsForCheckpoint.length > 0;

  const countAfterMetadata = useMemo(() => {
    return activeCheckpointMetadata.length + (shouldShowHelp ? 4 : 3) + (hasStandards ? 1 : 0);
  }, [activeCheckpointMetadata, shouldShowHelp, hasStandards]);

  const exceptionDetailsRows = useMemo(() => {
    if (!checkpoint?.hasException || !checkpoint.exceptionDetails) {
      return [];
    }

    return Object.entries(checkpoint.exceptionDetails).reduce<
      Record<string, unknown>[]
    >((acc, [property, value]) => {
      if (value === undefined) {
        return acc;
      }

      acc.push({ property, value });
      return acc;
    }, []);
  }, [checkpoint?.exceptionDetails, checkpoint?.hasException]);

  const unscoredBadge = useMemo(() => {
    if (!checkpoint) return null;
    if (checkpoint.metadata.isScored) return null;

    const unscored = getUnscoredStatusDetailsFromMetadata(checkpoint.metadata);
    const unscoredType = normalizeUnscoredType(unscored?.unscoredType);
    const isMaintenance =
      unscoredType === 'maintenance' || checkpoint.metadata.isMaintenance;
    const daysLeftText = getDaysLeftText(unscored?.endDate);
    const reasonRaw = isMaintenance
      ? unscored?.reason || checkpoint.metadata.maintenance?.note || ''
      : unscored?.reason || '';
    const eventTimeRaw = isMaintenance
      ? unscored?.eventTime || checkpoint.metadata.maintenance?.eventTime
      : undefined;

    const labelBase = isMaintenance ? 'MAINTENANCE' : 'PREVIEW';
    const label = daysLeftText ? `${labelBase} - ${daysLeftText}` : labelBase;
    const versionRaw = checkpoint.metadata.version;
    const version =
      versionRaw === null || versionRaw === undefined || `${versionRaw}`.trim() === ''
        ? null
        : `${versionRaw}`;
    const tooltipId = `unscored-${checkpoint.metadata.id}-${version ?? ''}`.replace(
      /[^a-zA-Z0-9_-]/g,
      '-',
    );
    const cleanReason = (value?: string) =>
      (value ?? '')
        .toString()
        .replace(/-\s*maintenance\s*$/i, '')
        .trim();
    const formatDateTime = (iso?: string) => {
      if (!iso) return null;
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return null;
      return format(date, 'yyyy-MM-dd HH:mm');
    };
    const reasonText = cleanReason(reasonRaw);
    const startText = formatDateTime(unscored?.startDate);
    const endText = formatDateTime(unscored?.endDate);
    const updatedText = formatDateTime(eventTimeRaw);
    const hasDetails = Boolean(reasonText || startText || endText || updatedText);
    const badgeColor: 'orange' | 'gray' = isMaintenance ? 'orange' : 'gray';

    return {
      tooltipId,
      color: badgeColor,
      label,
      isMaintenance,
      reasonText,
      startText,
      endText,
      updatedText,
      hasDetails,
    };
  }, [checkpoint]);

  if (!checkpoint) {
    return (
      <div className="checkpointResult bg-[#2d2d2d] flex flex-col w-full h-full max-sm:h-auto">
        <div className="flex items-center gap-3 justify-between px-4 py-2 shadow-md max-sm:flex-col max-sm:items-start">
          <div className="flex flex-col">
            <p className="text-white font-medium">No checkpoint available</p>
            <p className="text-white text-sm">
              This evaluation response did not include checkpoints.
            </p>
          </div>
          <div className="flex items-center gap-2 actions-container max-sm:w-full max-sm:flex-wrap">
            {canSwitchEvaluationView
              ? (
                <CheckpointResultActions
                  canSwitchEvaluationView={canSwitchEvaluationView}
                  isMobileViewport={isMobileViewport}
                  activeView={activeView}
                  availableViews={availableViews}
                  onViewChange={onViewChange}
                  onOpenCheckpointNavigator={openCheckpointNavigator}
                  isActionsDropdownOpen={state.isActionsDropdownOpen}
                  onActionsDropdownChange={(isOpen) =>
                    handleStateUpdate({ isActionsDropdownOpen: isOpen })
                  }
                  onOpenReportBug={() =>
                    handleStateUpdate({ isReportBugOpen: true })
                  }
                  onShare={onShareClick}
                  docsUrl={hubDocsUrl}
                  showDocsItem
                  onExportCsv={exportCsv}
                  isExportingCsv={state.isExportingCsv}
                  exportCsvLabel={exportCsvLabel}
                  shouldShowAiAnalysisAction={false}
                  canRunAiAnalysis={false}
                  aiLoading={false}
                  onRunAiAnalysis={() => {}}
                />
              )
              : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkpointResult bg-[#2d2d2d] flex flex-col w-full h-full max-sm:h-auto">
      <div className="flex items-center gap-3 justify-between px-4 py-2 shadow-md max-sm:flex-col max-sm:items-start">
        <div className="flex gap-2 min-w-0 max-sm:w-full">
          <span className="">
            <CheckpointStatusIcon checkpoint={checkpoint} size={28} />
          </span>
          <div className="flex flex-col min-w-0">
            {isMobileViewport ? (
              <>
                <p className="text-white text-lg font-semibold max-sm:break-words">
                  {checkpoint.metadata.displayName}
                  {unscoredBadge ? (
                    <span id={unscoredBadge.tooltipId} className="ml-2 inline-flex align-middle">
                      <Tag color={unscoredBadge.color}>{unscoredBadge.label}</Tag>
                    </span>
                  ) : null}
                </p>
                <p className="mt-1 text-white text-sm max-sm:break-words">
                  {checkpoint.remarks}
                </p>
                <button
                  className="mt-2 w-max text-blue-400 text-xs cursor-pointer underline"
                  onClick={() => handleStateUpdate({ isRemarksOpen: true })}
                >
                  View full remarks
                </button>
              </>
            ) : (
              <p className="text-white font-medium max-sm:break-words">
                Results for{' '}
                <span className="font-semibold">
                  {checkpoint.metadata.displayName}
                </span>
                {unscoredBadge ? (
                  <span id={unscoredBadge.tooltipId} className="ml-2 inline-flex align-middle">
                    <Tag color={unscoredBadge.color}>{unscoredBadge.label}</Tag>
                  </span>
                ) : null}
              </p>
            )}
            {unscoredBadge ? (
              <Tooltip targetSelector={`#${unscoredBadge.tooltipId}`}>
                <TooltipBody slot="tooltipBody">
                  <div className="flex w-full max-w-[420px] flex-col gap-1 whitespace-pre-line break-words rounded-md border border-white/10 bg-[#232323] px-2 py-2 text-[0.75rem] leading-5 text-white shadow-[0_10px_24px_rgba(0,0,0,0.35)]">
                    {unscoredBadge.reasonText ? (
                      <div>
                        <span className="font-semibold">Reason:</span>{' '}
                        {unscoredBadge.reasonText}
                      </div>
                    ) : null}
                    {unscoredBadge.startText ? (
                      <div>
                        <span className="font-semibold">Start:</span>{' '}
                        {unscoredBadge.startText}
                      </div>
                    ) : null}
                    {unscoredBadge.endText ? (
                      <div>
                        <span className="font-semibold">End:</span> {unscoredBadge.endText}
                      </div>
                    ) : null}
                    {unscoredBadge.updatedText ? (
                      <div>
                        <span className="font-semibold">Updated:</span>{' '}
                        {unscoredBadge.updatedText}
                      </div>
                    ) : null}
                    {!unscoredBadge.hasDetails ? (
                      <div>
                        {unscoredBadge.isMaintenance
                          ? 'No details available.'
                          : 'Checkpoint is currently unscored.'}
                      </div>
                    ) : null}
                  </div>
                </TooltipBody>
              </Tooltip>
            ) : null}
            {!isMobileViewport ? (
              <p className="text-white text-sm max-sm:break-words">
                {checkpoint.metadata.description}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 actions-container max-sm:w-full max-sm:flex-wrap">
          <CheckpointResultActions
            canSwitchEvaluationView={canSwitchEvaluationView}
            isMobileViewport={isMobileViewport}
            activeView={activeView}
            availableViews={availableViews}
            onViewChange={onViewChange}
            onOpenCheckpointNavigator={openCheckpointNavigator}
            isActionsDropdownOpen={state.isActionsDropdownOpen}
            onActionsDropdownChange={(isOpen) =>
              handleStateUpdate({ isActionsDropdownOpen: isOpen })
            }
            onOpenReportBug={() =>
              handleStateUpdate({ isReportBugOpen: true })
            }
            onShare={onShareClick}
            docsUrl={
              checkpoint.metadata.url
                ? `${checkpoint.metadata.url}#evaluation`
                : null
            }
            showDocsItem={Boolean(checkpoint.metadata.url)}
            onExportCsv={exportCsv}
            isExportingCsv={state.isExportingCsv}
            exportCsvLabel={exportCsvLabel}
            shouldShowAiAnalysisAction={shouldShowAiAnalysisAction}
            canRunAiAnalysis={canRunAiAnalysis}
            aiLoading={aiLoading}
            onRunAiAnalysis={() => {
              void RunAiAnalysis();
            }}
          />
        </div>
      </div>
      <CheckpointResultBody
        checkpoint={checkpoint}
        activeCheckpointMetadata={activeCheckpointMetadata}
        hasStandards={hasStandards}
        standardsForCheckpoint={standardsForCheckpoint}
        shouldShowHelp={shouldShowHelp}
        countAfterMetadata={countAfterMetadata}
        onOpenRemarks={() => handleStateUpdate({ isRemarksOpen: true })}
        onOpenAssessmentData={() =>
          handleStateUpdate({ isAssessmentDataOpen: true })
        }
        onOpenExceptionDetails={() =>
          handleStateUpdate({ isExceptionDetailsOpen: true })
        }
        exceptionDetailsRows={exceptionDetailsRows}
        canOpenAssessmentData={canOpenAssessmentData}
      />
      <CheckpointResultPanels
        checkpoint={checkpoint}
        isRemarksOpen={state.isRemarksOpen}
        onCloseRemarks={() => handleStateUpdate({ isRemarksOpen: false })}
        isAiPanelOpen={state.isAiPanelOpen}
        onCloseAiPanel={() => handleStateUpdate({ isAiPanelOpen: false })}
        aiLoading={aiLoading}
        aiResult={aiResult}
      />

      {renderAssessmentDataDrawer ? (
        renderAssessmentDataDrawer({
          checkpoint,
          evaluation,
          isOpen: state.isAssessmentDataOpen,
          onClose: () => handleStateUpdate({ isAssessmentDataOpen: false }),
        })
      ) : (
        <AssessmentData
          checkpoint={checkpoint}
          evaluation={evaluation}
          isOpen={state.isAssessmentDataOpen}
          onClose={() => handleStateUpdate({ isAssessmentDataOpen: false })}
        />
      )}
      <AssessmentData
        checkpoint={checkpoint}
        evaluation={evaluation}
        isOpen={state.isExceptionDetailsOpen}
        onClose={() => handleStateUpdate({ isExceptionDetailsOpen: false })}
        title="Checkpoint Exception Details"
        localData={exceptionDetailsRows}
        fetchRemote={false}
        errorTitle="Error loading exception details"
      />
      <ReportBug
        isOpen={state.isReportBugOpen}
        onClose={() => handleStateUpdate({ isReportBugOpen: false })}
        checkpoint={checkpoint}
        evaluation={evaluation}
      />
    </div>
  );
};

export default CheckpointResult;
